import React, { useEffect, useState } from "react";
import MessengerLayout        from "../../../layout/MessengerLayout";
import AttendanceFilters      from "./filters/AttendanceFilters";
import ComplianceOverview     from "./widgets/ComplianceOverview";
import AnomaliesPanel         from "./widgets/AnomaliesPanel";
import DailyOpsBoard          from "./widgets/DailyOpsBoard";
import RegularisationQueue    from "./widgets/RegularisationQueue";
import OvertimePanel          from "./widgets/OvertimePanel";
import RosterCoverage         from "./widgets/RosterCoverage";
import GeoIntegrity           from "./widgets/GeoIntegrity";
import LeaveOverlay           from "./widgets/LeaveOverlay";
import TrendsPanel            from "./widgets/TrendsPanel";
import AttendanceSettingsPage  from "./AttendanceSettingsPage";
import RotaSchedulerPage      from "../../../pages/rota/RotaSchedulerPage";
import AdminRotaSettingsPanel from "./AdminRotaSettingsPanel";
import { toast }              from "../../ui/Toast";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

function buildParams(filters) {
  const p = new URLSearchParams({ from: filters.from, to: filters.to });
  if (filters.location   !== "ALL") p.append("location",   filters.location);
  if (filters.department !== "ALL") p.append("department", filters.department);
  if (filters.empType    !== "ALL") p.append("empType",    filters.empType);
  if (filters.shift      !== "ALL") p.append("shift",      filters.shift);
  return p.toString();
}

export default function AdminAttendancePage() {
  const [tab, setTab] = useState("dashboard");
  const [filters, setFilters] = useState({
    from:       new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10),
    to:         new Date().toISOString().slice(0, 10),
    location:   "ALL",
    department: "ALL",
    empType:    "ALL",
    shift:      "ALL",
  });

  const [kpis,    setKpis]    = useState(null);
  const [data,    setData]    = useState({
    compliance: [], anomalies: [], daily: null, regularisations: [],
    overtime: [], roster: null, geo: null, leave: null, trends: null,
  });
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    let alive = true;

    const run = () => {
      setLoading(true);
      setError(null);
      const qs    = buildParams(filters);
      const today = new Date().toISOString().slice(0, 10);

      Promise.all([
        fetch(`${API_BASE}/api/admin/attendance/kpis?${qs}`,              { headers: authHeaders() }).then(r => r.json()),
        fetch(`${API_BASE}/api/admin/attendance/compliance?${qs}`,        { headers: authHeaders() }).then(r => r.json()),
        fetch(`${API_BASE}/api/admin/attendance/anomalies?${qs}`,         { headers: authHeaders() }).then(r => r.json()),
        fetch(`${API_BASE}/api/admin/attendance/daily?date=${today}`,     { headers: authHeaders() }).then(r => r.json()),
        fetch(`${API_BASE}/api/admin/attendance/regularisations?${qs}`,   { headers: authHeaders() }).then(r => r.json()),
        fetch(`${API_BASE}/api/admin/attendance/overtime?${qs}`,          { headers: authHeaders() }).then(r => r.json()),
        fetch(`${API_BASE}/api/admin/attendance/roster?${qs}`,            { headers: authHeaders() }).then(r => r.json()),
        fetch(`${API_BASE}/api/admin/attendance/geo`,                     { headers: authHeaders() }).then(r => r.json()),
        fetch(`${API_BASE}/api/admin/attendance/leave?${qs}`,             { headers: authHeaders() }).then(r => r.json()),
        fetch(`${API_BASE}/api/admin/attendance/trends?months=6`,         { headers: authHeaders() }).then(r => r.json()),
      ])
        .then(([kpis, compliance, anomalies, daily, regularisations, overtime, roster, geo, leave, trends]) => {
          if (!alive) return;
          setKpis(kpis);
          setData({ compliance, anomalies, daily, regularisations, overtime, roster, geo, leave, trends });
        })
        .catch(e => { if (alive) setError(e.message); })
        .finally(() => { if (alive) setLoading(false); });
    };

    run();
    const handleKiraAction = (e) => {
      const t = e.detail?.action_type || "";
      if (["attendance_checkin", "attendance_checkout", "attendance_regularise"].includes(t)) {
        run();
      }
    };
    window.addEventListener("kira_action_complete", handleKiraAction);
    return () => {
      alive = false;
      window.removeEventListener("kira_action_complete", handleKiraAction);
    };
  }, [filters]);

  const patchReg = (id, status) =>
    setData(d => ({ ...d, regularisations: d.regularisations.map(r => r.id === id ? { ...r, status } : r) }));

  const approveReg = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/attendance/regularisations/${id}/approve`, { method: "POST", headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      patchReg(id, "APPROVED");
      toast.success("Regularisation approved");
    } catch {
      toast.error("Failed to approve");
    }
  };
  const rejectReg = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/attendance/regularisations/${id}/reject`, { method: "POST", headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      patchReg(id, "REJECTED");
      toast.success("Regularisation rejected");
    } catch {
      toast.error("Failed to reject");
    }
  };

  const patchOT = (id, status) =>
    setData(d => ({ ...d, overtime: d.overtime.map(x => x.id === id ? { ...x, status } : x) }));

  const approveOT = async (id, comment) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/attendance/overtime/${id}/approve`, {
        method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ comment }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      patchOT(id, "APPROVED");
      toast.success("Overtime approved");
    } catch {
      toast.error("Failed to approve overtime");
    }
  };
  const rejectOT = async (id, comment) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/attendance/overtime/${id}/reject`, {
        method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ comment }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      patchOT(id, "REJECTED");
      toast.success("Overtime rejected");
    } catch {
      toast.error("Failed to reject overtime");
    }
  };

  return (
    <MessengerLayout>
      <div className="p-6 text-white space-y-5">

        <div>
          <h1 className="text-2xl font-bold text-[#00ff90]">Attendance</h1>
          <p className="text-white/40 text-sm mt-0.5">Organisation-wide attendance, anomalies and workforce ops</p>
        </div>

        <div className="flex gap-2">
          {[
            { v: "dashboard", label: "Dashboard" },
            { v: "rota",      label: "Rota" },
            { v: "settings",  label: "Settings" },
          ].map(t => (
            <button key={t.v} onClick={() => setTab(t.v)}
              className={`text-xs px-3 py-2 rounded-lg border transition-colors ${
                tab === t.v
                  ? "bg-[#00ff90]/15 border-[#00ff90]/40 text-[#00ff90]"
                  : "border-white/10 bg-white/5 text-white/40 hover:text-white"
              }`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "dashboard" && (
          <>
            <AttendanceFilters filters={filters} setFilters={setFilters} kpis={kpis} loading={loading} />

            {error && (
              <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-red-300 text-sm">⚠ {error}</div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <ComplianceOverview  loading={loading} rows={data.compliance} />
              <AnomaliesPanel      loading={loading} rows={data.anomalies}  />
              <DailyOpsBoard       loading={loading} data={data.daily}      />
              <RegularisationQueue loading={loading} items={data.regularisations} onApprove={approveReg} onReject={rejectReg} />
              <OvertimePanel       loading={loading} items={data.overtime}  onApprove={approveOT} onReject={rejectOT} />
              <RosterCoverage      loading={loading} data={data.roster}     />
              <GeoIntegrity        loading={loading} data={data.geo}        />
              <LeaveOverlay        loading={loading} data={data.leave}      />
              <TrendsPanel         loading={loading} data={data.trends}     />
            </div>
          </>
        )}

        {tab === "rota" && (
          <div className="space-y-5">
            <AdminRotaSettingsPanel />
            <RotaSchedulerPage embedded={true} />
          </div>
        )}

        {tab === "settings" && <AttendanceSettingsPage />}

      </div>
    </MessengerLayout>
  );
}